import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CastError } from '../../../../chromecast/cast-error';
import { castAtsStream } from '../../../../chromecast/utils/cast-ats-stream';
import { HeaderDisplayType, HeaderInteractionType } from '../../../../interfaces';
import { getAggregatedTitleImage } from '../../../../queries/ats/common';
import { PlayablePlaybackOption } from '../../../../queries/ats/titles/types/playback-option';
import { PlayableTitle } from '../../../../queries/ats/titles/types/title';
import Header from '../../../../views/app/Header';
import StickyHeader from '../../../../views/app/StickyHeader';
import { DetailsLink } from '../../../../views/ats-details/details-link';
import PlayerAssetCard from '../../UI/PlayerAssetCard';
import { ChromecastPoster } from '../chromecast-poster';

interface ChromecastVodSenderProps {
  title: PlayableTitle;
  playbackOption: PlayablePlaybackOption;
  session: cast.framework.CastSession;
  position: number | undefined;
  isTrailer?: boolean;
}

export function ChromecastVodSender({
  title,
  playbackOption,
  session,
  position = 0,
  isTrailer = false,
}: ChromecastVodSenderProps) {
  const history = useHistory();
  const shouldFadeOut = useSelector((state) => state.app.shouldFadeOut);
  const [error, setError] = useState<unknown>();

  const castStream = useCallback(async () => {
    try {
      await castAtsStream({
        title,
        playbackOption,
        session,
        position,
        isTrailer,
      });
    } catch (err) {
      setError(err);
    }
  }, [isTrailer, playbackOption, position, session, title]);

  useEffect(() => {
    castStream();
  }, [castStream]);

  if (error instanceof CastError) {
    throw error;
  }

  if (error) {
    const message = error instanceof Error ? error.message : JSON.stringify(error);
    throw new CastError(message, { sessionId: session.getSessionId(), cause: error });
  }

  return (
    <div className="ats-player">
      <StickyHeader overPlayer={true} />
      <Header
        displayType={HeaderDisplayType.Player}
        interactionType={HeaderInteractionType.Back}
        closeCallback={() => {
          history.goBack();
        }}
      />

      <ChromecastPoster src={getAggregatedTitleImage(title.masterId, 'wide', 2000)} />

      {shouldFadeOut ? null : (
        <DetailsLink className="player-asset-card-link" id={title.masterId} type={title.type}>
          <PlayerAssetCard
            image={getAggregatedTitleImage(title.masterId, 'wide', 500)}
            currentTitle={title.titleName}
          />
        </DetailsLink>
      )}
    </div>
  );
}
